import React, { FunctionComponent } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/RenderType";
import {
    IBrand,
    IRetailMatchJobResult,
    IStoreScoreRecord,
} from "../../../../Models/Models";
import {
    retailMatchLongAddressSelector,
    retailMatchReportDataSelector,
} from "../../../../Reducers/Selectors/RetailMatchSelectors";
import { getBenchmarkAddressOverride } from "../../../../Util/RetailMatch/RetailMatchUtil";
import { ReportInnerWrapper } from "../../../Common";
import RetailMatchHeader from "./RetailMatchHeader/RetailMatchHeader";
import RetailMatchHouseholdComposition from "./RetailMatchHouseholdComposition";
import { RetailMatchIntroduction } from "./RetailMatchIntroduction";
import { RetailMatchKeyMetrics } from "./RetailMatchKeyMetrics";
import { RetailMatchLocationsAnalyzed } from "./RetailMatchLocationsAnalyzed";
import { RetailMatchPsychProf } from "./RetailMatchPsychProf";
import { RetailMatchStickyNav } from "./RetailMatchStickyNav";

interface RetailMatchReportProps {
  isSharing?: boolean;
  renderType: RenderType;
  jobResult: IRetailMatchJobResult;
  brand: IBrand;
  isScout?: boolean;
  onPrintClick: () => void;
  onShareClick: () => void;
}

const RetailMatchReport: FunctionComponent<RetailMatchReportProps> = (
    props: RetailMatchReportProps
) => {
    const { isSharing, renderType, onPrintClick, onShareClick, brand } = props;

    const { jobResult, benchmarkRecord } = useSelector(
        retailMatchReportDataSelector,
        shallowEqual
    );

    const benchmarkAddressOverride: IStoreScoreRecord
    = getBenchmarkAddressOverride(jobResult, benchmarkRecord);
    const longAddress = useSelector(retailMatchLongAddressSelector);

    return (
        <ReportInnerWrapper>
            <RetailMatchHeader
                multiBrand={false}
                locationAddress={longAddress}
                job={jobResult.job}
                isSharing={isSharing}
                onPrintClick={onPrintClick}
                onShareClick={onShareClick}
            />
            <RetailMatchStickyNav stuckClass={""} isSharing={isSharing} isScout={props.isScout} />
            <RetailMatchIntroduction
                multiBrand={false}
                brand={brand}
                jobResult={jobResult}
                locationAddress={longAddress}
                isSharing={isSharing}
                benchmarkAddressOverride={benchmarkAddressOverride}
                renderType={renderType}
            />
            <RetailMatchKeyMetrics
                brand={brand}
                jobResult={jobResult}
                benchmarkAddressOverride={benchmarkAddressOverride}
                locationAddress={benchmarkAddressOverride.addr}
                renderType={renderType}
                app={DigitalShareAppType.RetailMatch}
                itemType={DigitalShareItemType.KeyMetrics}
            />
            <RetailMatchLocationsAnalyzed
                key={brand.brandId}
                jobResult={props.jobResult}
                benchmarkRecord={benchmarkAddressOverride}
                brand={brand}
                renderType={renderType}
                app={DigitalShareAppType.RetailMatch}
            />
            <RetailMatchPsychProf
                brand={brand}
                locationAddress={benchmarkAddressOverride.addr}
                chartData={jobResult.chartData}
                benchmarkRecord={benchmarkAddressOverride}
                renderType={renderType}
                app={DigitalShareAppType.RetailMatch}
                itemType={DigitalShareItemType.PsychographicProfile}
            />
            <RetailMatchHouseholdComposition
                brand={props.brand}
                chartData={jobResult.chartData}
                locationAddress={benchmarkAddressOverride.addr}
                renderType={renderType}
            />
        </ReportInnerWrapper>
    );
};

export default RetailMatchReport;
